import { useRouterState } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

export default function NavigationProgress() {
  const isLoading = useRouterState({ select: s => s.status === 'pending' });
  const [width, setWidth] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Distinguishes "never navigated" from "navigation just finished", so the
  // completion branch doesn't run on mount and flash a full bar.
  const wasLoading = useRef(false);

  useEffect(() => {
    if (isLoading) {
      wasLoading.current = true;
      // rAF rather than a bare `setWidth(10)`: setting state synchronously in
      // an effect body cascades renders. One frame is imperceptible, and the
      // bar still shows up on the next paint.
      const raf = requestAnimationFrame(() => setWidth(10));

      intervalRef.current = setInterval(() => {
        setWidth(w => {
          if (w >= 85) return w;
          // Slow down as it approaches 85%
          return w + (85 - w) * 0.08;
        });
      }, 100);

      return () => {
        cancelAnimationFrame(raf);
        if (intervalRef.current) clearInterval(intervalRef.current);
      };
    }

    if (!wasLoading.current) return;
    wasLoading.current = false;

    if (intervalRef.current) clearInterval(intervalRef.current);
    const raf = requestAnimationFrame(() => setWidth(100));
    timerRef.current = setTimeout(() => setWidth(0), 300);

    return () => {
      cancelAnimationFrame(raf);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isLoading]);

  // Derived, not state: the bar is on screen whenever a navigation is in
  // flight or the finishing animation still has width to unwind.
  if (!isLoading && width === 0) return null;

  return (
    // `absolute`, not `fixed`: the viewport top belongs to the opt-out banner,
    // and this bar shares its background colour, so it vanished behind it. The
    // shell is the positioning context, so the bar rides the top of the app
    // itself whether or not the banner is showing.
    <div className='absolute top-0 left-0 right-0 z-[9999] h-0.5 pointer-events-none'>
      <div
        className='h-full bg-primary transition-[width] ease-out'
        style={{
          width: `${width}%`,
          transitionDuration: width === 100 ? '200ms' : '100ms',
        }}
      />
    </div>
  );
}
